import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  CircularProgress,
  Alert,
  LinearProgress,
  Tooltip,
  Avatar,
  IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  OpenInNew as OpenInNewIcon,
  Assignment as AssignmentIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Circle as CircleIcon,
} from '@mui/icons-material';
import { Select, MenuItem, FormControl } from '@mui/material';
import { getClients, getClient, getUsers, assignClientSales } from '../services/api';
import PageSearchBar from '../components/PageSearchBar';
import { useAuth } from '../contexts/AuthContext';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmtCurrency = (val) => {
  const n = parseFloat(val) || 0;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
};

const fmtCurrencyFull = (val) => {
  const n = parseFloat(val) || 0;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
};

const PHASE_LABELS = ['Proposal', 'Engineering & Kick-off', 'Pre-Wiring', 'Pre-Final', 'Final', 'Final Billing', 'AfterCare'];

const healthColor = (status) => {
  if (status === 'green') return '#2e7d32';
  if (status === 'yellow') return '#f57c00';
  if (status === 'red') return '#c62828';
  return '#9e9e9e';
};

const healthLabel = (status) => {
  if (status === 'green') return 'On Track';
  if (status === 'yellow') return 'At Risk';
  if (status === 'red') return 'Critical';
  return 'Unknown';
};

// ─── Client List View ─────────────────────────────────────────────────────────

function ClientListView() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('client_name');
  const [salesUsers, setSalesUsers] = useState([]);
  const [dir, setDir] = useState('asc');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getClients({ search: search || undefined, sort, dir });
      setClients(res.data || []);
    } catch (err) {
      setError('Failed to load clients.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, sort, dir]);

  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  // Load sales users for assignment dropdown
  useEffect(() => {
    getUsers()
      .then((res) => {
        const users = (res.data || []).filter(u => u.role === 'Sales' || u.role === 'Management');
        users.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setSalesUsers(users);
      })
      .catch(() => {});
  }, []);

  const handleAssignSales = async (arCode, salesId) => {
    try {
      const res = await assignClientSales(arCode, salesId);
      // Update local state
      setClients(prev => prev.map(c =>
        c.ar_number === arCode ? { ...c, sales_rep_name: res.data.sales_name } : c
      ));
    } catch (err) {
      console.error('Failed to assign sales rep:', err);
      alert('Failed to assign sales rep');
    }
  };

  const handleSort = (field) => {
    if (sort === field) {
      setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(field);
      setDir('asc');
    }
  };

  // Summary stats
  const totalClients = clients.length;
  const totalActiveProjects = clients.reduce((s, c) => s + (parseInt(c.active_project_count) || 0), 0);
  const totalValue = clients.reduce((s, c) => s + (parseFloat(c.total_value) || 0), 0);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 300, color: '#0D2C54', letterSpacing: '-0.5px' }}>
          Clients
        </Typography>
        <Typography variant="body2" sx={{ color: '#8898A4', mt: 0.5 }}>
          Unified view of all client accounts and their projects
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Paper elevation={2} sx={{ p: 2, borderLeft: '4px solid #0D2C54' }}>
            <Typography variant="caption" color="text.secondary">Total Clients</Typography>
            <Typography variant="h4" fontWeight="bold" color="#0D2C54">{totalClients}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper elevation={2} sx={{ p: 2, borderLeft: '4px solid #2e7d32' }}>
            <Typography variant="caption" color="text.secondary">Active Projects</Typography>
            <Typography variant="h4" fontWeight="bold" color="#2e7d32">{totalActiveProjects}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper elevation={2} sx={{ p: 2, borderLeft: '4px solid #FF6B35' }}>
            <Typography variant="caption" color="text.secondary">Total Contract Value</Typography>
            <Typography variant="h4" fontWeight="bold" color="#0D2C54">{fmtCurrency(totalValue)}</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Search */}
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <PageSearchBar
          placeholder="Search by client name or AR code…"
          value={search}
          onChange={(v) => setSearch(v)}
        />
      </Paper>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : clients.length === 0 ? (
        <Alert severity="info">No clients found.</Alert>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table size="small">
            <TableHead sx={{ backgroundColor: '#0D2C54' }}>
              <TableRow>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>
                  <TableSortLabel
                    active={sort === 'client_name'}
                    direction={sort === 'client_name' ? dir : 'asc'}
                    onClick={() => handleSort('client_name')}
                    sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#ccc !important' } }}
                  >
                    Client Name
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>AR Code</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">
                  <TableSortLabel
                    active={sort === 'project_count'}
                    direction={sort === 'project_count' ? dir : 'asc'}
                    onClick={() => handleSort('project_count')}
                    sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#ccc !important' } }}
                  >
                    Projects
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">Active</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>
                  <TableSortLabel
                    active={sort === 'sales_rep_name'}
                    direction={sort === 'sales_rep_name' ? dir : 'asc'}
                    onClick={() => handleSort('sales_rep_name')}
                    sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#ccc !important' } }}
                  >
                    Sales Rep
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="right">
                  <TableSortLabel
                    active={sort === 'total_value'}
                    direction={sort === 'total_value' ? dir : 'asc'}
                    onClick={() => handleSort('total_value')}
                    sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#ccc !important' } }}
                  >
                    Total Contract Value
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clients.map((c, idx) => (
                <TableRow
                  key={c.ar_number}
                  hover
                  onClick={() => navigate(`/clients/${encodeURIComponent(c.ar_number)}`)}
                  sx={{ cursor: 'pointer', backgroundColor: idx % 2 === 0 ? 'transparent' : 'action.hover' }}
                >
                  <TableCell>
                    <Typography fontWeight="medium">{c.client_name || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={c.ar_number} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={c.project_count} size="small" color="primary" />
                  </TableCell>
                  <TableCell align="center">
                    {parseInt(c.active_project_count) > 0 ? (
                      <Chip label={c.active_project_count} size="small" color="success" />
                    ) : (
                      <Typography color="text.secondary" variant="body2">—</Typography>
                    )}
                  </TableCell>
                  <TableCell onClick={(e) => !c.sales_rep_name && e.stopPropagation()}>
                    {c.sales_rep_name ? (
                      <Typography variant="body2">{c.sales_rep_name}</Typography>
                    ) : (
                      <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select
                          displayEmpty
                          value=""
                          onChange={(e) => {
                            e.stopPropagation();
                            if (e.target.value) handleAssignSales(c.ar_number, e.target.value);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          sx={{ fontSize: '0.8rem', height: 32, color: 'text.secondary' }}
                          renderValue={() => <em>Assign…</em>}
                        >
                          {salesUsers.map((u) => (
                            <MenuItem key={u.id} value={u.id} sx={{ fontSize: '0.85rem' }}>{u.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="medium">{fmtCurrencyFull(c.total_value)}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

// ─── Project Card ─────────────────────────────────────────────────────────────

function ProjectCard({ project }) {
  const navigate = useNavigate();
  const laborPct = project.labor_hours_sold > 0
    ? Math.round((project.labor_hours_used / project.labor_hours_sold) * 100)
    : 0;
  const laborColor = laborPct > 100 ? 'error' : laborPct > 85 ? 'warning' : 'success';

  return (
    <Card
      elevation={2}
      sx={{
        cursor: 'pointer',
        borderLeft: `4px solid ${healthColor(project.health_status)}`,
        '&:hover': { elevation: 4, boxShadow: 4 },
        transition: 'box-shadow 0.2s',
      }}
    >
      <CardActionArea onClick={() => navigate(`/projects/${project.id}`)}>
        <CardContent sx={{ pb: 1 }}>
          {/* Header row */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Box sx={{ flex: 1, mr: 1 }}>
              {project.primary_job_number && (
                <Typography variant="caption" color="primary.main" fontWeight="bold">
                  Job #{project.primary_job_number}
                </Typography>
              )}
              <Typography variant="subtitle1" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                {project.name}
              </Typography>
            </Box>
            <Tooltip title={healthLabel(project.health_status)}>
              <CircleIcon sx={{ color: healthColor(project.health_status), fontSize: 14, mt: 0.5 }} />
            </Tooltip>
          </Box>

          {/* Phase & PM */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
            <Chip
              label={PHASE_LABELS[project.current_phase] || `Phase ${project.current_phase}`}
              size="small"
              sx={{ backgroundColor: '#E3F2FD', color: '#1565C0', fontSize: '0.7rem' }}
            />
            {project.pm_name && (
              <Chip
                label={project.pm_name}
                size="small"
                avatar={<Avatar sx={{ width: 16, height: 16, fontSize: '0.6rem' }}>{project.pm_name[0]}</Avatar>}
                variant="outlined"
                sx={{ fontSize: '0.7rem' }}
              />
            )}
          </Box>

          {/* Labor bar */}
          {project.labor_hours_sold > 0 && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                <Typography variant="caption" color="text.secondary">Labor</Typography>
                <Typography variant="caption" color="text.secondary">
                  {project.labor_hours_used || 0}h / {project.labor_hours_sold}h ({laborPct}%)
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(laborPct, 100)}
                color={laborColor}
                sx={{ height: 5, borderRadius: 2 }}
              />
            </Box>
          )}

          {/* Contract value */}
          {parseFloat(project.sage_contract_amount) > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {fmtCurrencyFull(project.sage_contract_amount)}
            </Typography>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

// ─── Combined Timeline ────────────────────────────────────────────────────────

function CombinedTimeline({ projects }) {
  // Build a simple phase-bar timeline per project
  // Each project row shows current_phase filled + remaining unfilled

  return (
    <Box>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
        Combined Timeline
      </Typography>
      <TableContainer component={Paper} elevation={1} sx={{ mb: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 700, width: 200 }}>Project</TableCell>
              {PHASE_LABELS.map((label, i) => (
                <TableCell key={i} align="center" sx={{ fontWeight: 600, fontSize: '0.7rem', px: 0.5 }}>
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {projects.map((p) => (
              <TableRow key={p.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.78rem' }}>
                    {p.name}
                  </Typography>
                  {p.primary_job_number && (
                    <Typography variant="caption" color="primary.main">
                      #{p.primary_job_number}
                    </Typography>
                  )}
                </TableCell>
                {PHASE_LABELS.map((_, phaseIdx) => {
                  const isCurrent = phaseIdx === p.current_phase;
                  const isCompleted = phaseIdx < p.current_phase;
                  let bg = 'transparent';
                  if (isCompleted) bg = '#c8e6c9'; // green
                  if (isCurrent) bg = '#0D2C54';   // ACS navy

                  return (
                    <TableCell
                      key={phaseIdx}
                      align="center"
                      sx={{ backgroundColor: bg, px: 0.5, py: 1, borderRadius: isCurrent ? 1 : 0 }}
                    >
                      {isCurrent && (
                        <Typography variant="caption" sx={{ color: '#fff', fontWeight: 700, fontSize: '0.65rem' }}>
                          ●
                        </Typography>
                      )}
                      {isCompleted && (
                        <Typography variant="caption" sx={{ color: '#2e7d32', fontSize: '0.65rem' }}>
                          ✓
                        </Typography>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: 1, backgroundColor: '#c8e6c9' }} />
          <Typography variant="caption" color="text.secondary">Completed</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: 1, backgroundColor: '#0D2C54' }} />
          <Typography variant="caption" color="text.secondary">Current Phase</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: 1, backgroundColor: '#e0e0e0' }} />
          <Typography variant="caption" color="text.secondary">Not Started</Typography>
        </Box>
      </Box>
    </Box>
  );
}

// ─── Combined Contacts ────────────────────────────────────────────────────────

function CombinedContacts({ contacts }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? contacts : contacts.slice(0, 8);

  if (!contacts.length) {
    return (
      <Box>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>Combined Contacts</Typography>
        <Typography color="text.secondary">No contacts found.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
        Combined Contacts ({contacts.length})
      </Typography>
      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Company</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Projects</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((c, i) => (
              <TableRow key={i} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">{c.contact_name || '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{c.role || '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{c.company_name || '—'}</Typography>
                </TableCell>
                <TableCell>
                  {c.email ? (
                    <Typography variant="body2" component="a" href={`mailto:${c.email}`} sx={{ color: 'primary.main', textDecoration: 'none' }}>
                      {c.email}
                    </Typography>
                  ) : '—'}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{c.phone || '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {(c.projects || []).map((p) => (
                      <Chip
                        key={p.id}
                        label={p.job_number || p.name}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.65rem', height: 18 }}
                      />
                    ))}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {contacts.length > 8 && (
        <Button
          size="small"
          onClick={() => setExpanded((e) => !e)}
          endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{ mt: 1 }}
        >
          {expanded ? 'Show less' : `Show all ${contacts.length} contacts`}
        </Button>
      )}
    </Box>
  );
}

// ─── Combined Financials ──────────────────────────────────────────────────────

function CombinedFinancials({ financials }) {
  const { user } = useAuth();
  const hasProfitability = user?.profitabilityAccess === true;
  const { total_contract, total_billed, total_cost, total_labor_sold, total_labor_used, per_project } = financials;

  return (
    <Box>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
        Combined Financials
      </Typography>

      {/* Summary row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Contract', value: fmtCurrencyFull(total_contract), color: '#0D2C54' },
          { label: 'Total Billed', value: fmtCurrencyFull(total_billed), color: '#1565C0' },
          // Total Cost is profitability-restricted. Tom Clancy 2026-05-14.
          ...(hasProfitability ? [{ label: 'Total Cost', value: fmtCurrencyFull(total_cost), color: '#c62828' }] : []),
          { label: 'Labor (Sold / Used)', value: `${total_labor_sold}h / ${total_labor_used}h`, color: '#2e7d32' },
        ].map((item) => (
          <Grid item xs={6} sm={3} key={item.label}>
            <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" display="block">{item.label}</Typography>
              <Typography variant="subtitle1" fontWeight="bold" color={item.color}>{item.value}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Per-project table */}
      {per_project && per_project.length > 1 && (
        <TableContainer component={Paper} elevation={1}>
          <Table size="small">
            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Project</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Contract</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Billed</TableCell>
                {hasProfitability && <TableCell sx={{ fontWeight: 700 }} align="right">Cost</TableCell>}
                <TableCell sx={{ fontWeight: 700 }} align="right">Labor (Sold/Used)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {per_project.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">{p.name}</Typography>
                    {p.job_number && (
                      <Typography variant="caption" color="primary.main">#{p.job_number}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{fmtCurrencyFull(p.contract)}</TableCell>
                  <TableCell align="right">{fmtCurrencyFull(p.billed)}</TableCell>
                  {hasProfitability && <TableCell align="right">{fmtCurrencyFull(p.cost)}</TableCell>}
                  <TableCell align="right">
                    <Typography variant="body2">{p.labor_sold}h / {p.labor_used}h</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

// ─── OptiPortal Reports ───────────────────────────────────────────────────────

function OptiPortalReports({ jobs }) {
  if (!jobs || jobs.length === 0) return null;

  return (
    <Box>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
        OptiPortal Reports
      </Typography>
      <Grid container spacing={1}>
        {jobs.map((j) => (
          <Grid item xs={12} sm={6} md={4} key={j.project_id}>
            <Paper elevation={1} sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="body2" fontWeight="medium">{j.project_name}</Typography>
                <Typography variant="caption" color="primary.main">Job #{j.job_number}</Typography>
              </Box>
              {j.optiportal_url && (
                <Tooltip title="Open in OptiPortal">
                  <IconButton
                    size="small"
                    onClick={() => window.open(j.optiportal_url, '_blank')}
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

// ─── Deals Table ──────────────────────────────────────────────────────────────

function DealsTable({ deals }) {
  if (!deals || deals.length === 0) return null;

  return (
    <Box>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
        All Deals / Proposals ({deals.length})
      </Typography>
      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Deal Name</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Project</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Stage</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {deals.map((d, i) => (
              <TableRow key={i} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">{d.deal_name || d.name || '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{d.project_name || '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Chip label={d.stage || d.status || '—'} size="small" variant="outlined" />
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">
                    {d.amount || d.value ? fmtCurrencyFull(d.amount || d.value) : '—'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ─── Client Detail View ───────────────────────────────────────────────────────

function ClientDetailView({ arCode }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMgmt = user?.role === 'Management' || user?.roles?.includes('Management')
    || ['Management', 'Finance', 'PM', 'Sales'].includes(user?.role)
    || [1,2,3,4,5,6,7,8,16,17,18,19,20,21,34,43,77].includes(user?.id);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await getClient(arCode);
        setData(res.data);
      } catch (err) {
        setError(err.response?.status === 404
          ? 'No projects found for this AR code.'
          : 'Failed to load client data.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [arCode]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/clients')} sx={{ mb: 2 }}>
          Back to Clients
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const { client_name, projects = [], contacts = [], financials = {}, deals = [], opti_jobs = [] } = data || {};

  const activeProjects = projects.filter((p) => p.sage_status !== 'Closed' && p.bucket !== 'archive');

  return (
    <Box sx={{ p: 3 }}>
      {/* Back button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/clients')}
        sx={{ mb: 2, color: '#0D2C54' }}
      >
        All Clients
      </Button>

      {/* Header */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderLeft: '5px solid #0D2C54' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <BusinessIcon sx={{ color: '#0D2C54', fontSize: 28 }} />
              <Typography variant="h4" fontWeight="bold" color="#0D2C54">
                {client_name}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Chip label={`AR: ${arCode}`} size="small" sx={{ backgroundColor: '#0D2C54', color: '#fff' }} />
              <Chip
                icon={<AssignmentIcon />}
                label={`${projects.length} project${projects.length !== 1 ? 's' : ''}`}
                size="small"
                color="primary"
                variant="outlined"
              />
              {activeProjects.length > 0 && (
                <Chip
                  label={`${activeProjects.length} active`}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>
          {isMgmt && financials.total_contract > 0 && (
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" color="text.secondary">Total Contract Value</Typography>
              <Typography variant="h5" fontWeight="bold" color="#0D2C54">
                {fmtCurrencyFull(financials.total_contract)}
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Projects Section */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
          Projects ({projects.length})
        </Typography>
        <Grid container spacing={2}>
          {projects.map((p) => (
            <Grid item xs={12} sm={6} md={4} key={p.id}>
              <ProjectCard project={p} />
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Combined Timeline */}
      {projects.length > 0 && (
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <CombinedTimeline projects={projects} />
        </Paper>
      )}

      {/* Combined Contacts */}
      {contacts.length > 0 && (
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <CombinedContacts contacts={contacts} />
        </Paper>
      )}

      {/* Combined Financials (management only) */}
      {isMgmt && (
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <CombinedFinancials financials={financials} />
        </Paper>
      )}

      {/* OptiPortal Reports */}
      {opti_jobs.length > 0 && (
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <OptiPortalReports jobs={opti_jobs} />
        </Paper>
      )}

      {/* All Deals */}
      {deals.length > 0 && (
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <DealsTable deals={deals} />
        </Paper>
      )}
    </Box>
  );
}

// ─── Main Export — handles both list and detail ───────────────────────────────

export default function Clients() {
  const { arCode } = useParams();

  if (arCode) {
    return <ClientDetailView arCode={decodeURIComponent(arCode)} />;
  }

  return <ClientListView />;
}
